import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * The content band: 1320px wide, with the rail the vertical rules sit on.
 *
 * Every section in the design is this band, so it is a component rather than a
 * repeated pair of utilities — and the rail width is a token, so the rules and
 * the content cannot drift apart.
 */
export function Band({ children, className }: { children: ReactNode; className?: string }) {
    return <div className={cn('mx-auto w-full max-w-band px-rail', className)}>{children}</div>
}

/**
 * A page section, hairlined off from the one below it.
 *
 * `edge` says which side carries the rule. The design mixes the two — a run of
 * sections is separated by bottom rules, and a section that follows a
 * full-bleed block carries a top rule instead — and doubling them up shows as
 * a two-pixel line.
 */
export function Section({
    children,
    edge = 'bottom',
    className,
}: {
    children: ReactNode
    edge?: 'bottom' | 'top' | 'none'
    className?: string
}) {
    return (
        <section
            className={cn(
                edge === 'bottom' && 'border-b border-hair',
                edge === 'top' && 'border-t border-hair',
                className,
            )}
        >
            {children}
        </section>
    )
}
